<template>
  <v-container fluid class="post-create-page">
    <section class="post-create-hero mb-6">
      <div class="post-create-hero__content">
        <div class="post-create-hero__eyebrow">AI Factory</div>
        <h1 class="post-create-hero__title">Test du process de création</h1>
        <p class="post-create-hero__copy">
          Configurez les sources, testez les prompts et publiez en gardant les
          outils de debug à portée.
        </p>
      </div>
      <div class="post-create-hero__actions">
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <rmr-action-button
                label="Cache Perplexity"
                size="sm"
                variant="translucent"
                :icon="icons.mdiRobotConfused"
                :loading="clearingPerplexityCache"
                @click="clearPerplexityCache"
              />
            </span>
          </template>
          <span>Supprime les réponses API en cache</span>
        </v-tooltip>

        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <rmr-action-button
                label="Cache Scraping"
                size="sm"
                variant="translucent"
                :icon="icons.mdiWebRemove"
                :loading="clearingScrapingCache"
                @click="clearScrapingCache"
              />
            </span>
          </template>
          <span>Supprime les contenus web en cache</span>
        </v-tooltip>
      </div>
    </section>

    <v-card class="post-create-section mb-6" outlined>
      <v-card-title
        class="post-create-section__title d-flex align-center justify-space-between"
        style="cursor: pointer"
        @click="toggleSourcesSection"
      >
        <span
          >1. Tests des prompts avant postqueue (sources &amp; cadrage)</span
        >
        <v-icon small>{{
          sourcesSectionExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
        }}</v-icon>
      </v-card-title>
      <v-card-text v-show="sourcesSectionExpanded">
        <SourcePromptLab @send-to-redaction="onSendToRedaction" />
      </v-card-text>
    </v-card>

    <v-card class="post-create-section mb-6" outlined>
      <v-card-title class="post-create-section__title">
        2. Tests des prompts après envoi postqueue (rédaction)
      </v-card-title>
      <v-card-text>
        <RedactionPromptLab
          :topic="redacInput.topic"
          :sources="redacInput.sources"
          :site-id="redacInput.siteId"
        />
      </v-card-text>
    </v-card>

    <v-card v-if="canShowPromptTesting" class="post-create-section" outlined>
      <v-card-title class="post-create-section__title">
        3. Publication WordPress
      </v-card-title>
      <v-card-text>
        <WordPressPublisher />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "@axios";
import { mdiRobotConfused, mdiWebRemove } from "@mdi/js";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import SourcePromptLab from "@/components/admin/ai-factory/SourcePromptLab.vue";
import RedactionPromptLab from "@/components/admin/ai-factory/RedactionPromptLab.vue";
import WordPressPublisher from "@/components/admin/ai-factory/WordPressPublisher.vue";

export default {
  name: "PostCreate",

  components: {
    RmrActionButton,
    SourcePromptLab,
    RedactionPromptLab,
    WordPressPublisher,
  },

  data() {
    return {
      isScrapingLoading: false,
      isPromptLoading: false,
      sourcesSectionExpanded: true,
      isInitialStepLoad: true,
      clearingPerplexityCache: false,
      clearingScrapingCache: false,
      redacInput: { topic: "", sources: [], siteId: null },
      icons: {
        mdiRobotConfused,
        mdiWebRemove,
      },
    };
  },

  computed: {
    ...mapGetters("aiFactory", [
      "getTopic",
      "getSources",
      "getScrapedSources",
      "getSites",
      "getSelectedSite",
      "getModels",
      "getSelectedModel",
      "getPromptResults",
      "isLoading",
      "getError",
    ]),

    topic: {
      get() {
        return this.getTopic;
      },
      set(value) {
        this.updateTopic(value);
      },
    },

    sources: {
      get() {
        return this.getSources.length > 0 ? this.getSources : [""];
      },
      set(value) {
        this.updateSources(value);
      },
    },

    selectedSite: {
      get() {
        return this.getSelectedSite;
      },
      set(value) {
        this.updateSelectedSite(value);
      },
    },

    selectedModel: {
      get() {
        return this.getSelectedModel;
      },
      set(value) {
        this.updateModelParams(value);
      },
    },

    sites() {
      return this.getSites;
    },
    models() {
      return this.getModels;
    },
    scrapedSources() {
      return this.getScrapedSources;
    },
    promptResults() {
      return this.getPromptResults;
    },
    canShowPromptTesting() {
      const hasTopic = !!(this.getTopic && this.getTopic.trim());
      const hasRealSource = (this.getSources || []).some((source) => {
        if (typeof source === "string") return !!source.trim();
        if (!source || typeof source !== "object") return false;
        return !!(
          (source.url && source.url.trim()) ||
          (source.content && source.content.trim())
        );
      });
      return hasTopic && hasRealSource;
    },
  },

  async mounted() {
    await Promise.all([this.fetchModels(), this.fetchPrompts()]);
    this.$nextTick(() => {
      this.isInitialStepLoad = false;
    });
  },

  watch: {
    canShowPromptTesting(newValue, oldValue) {
      if (this.isInitialStepLoad) return;
      if (newValue && !oldValue) {
        this.sourcesSectionExpanded = false;
      }
    },
  },

  methods: {
    ...mapActions("aiFactory", [
      "fetchModels",
      "fetchPrompts",
      "scrapeSources",
      "testPrompt",
      "updateTopic",
      "updateSources",
      "addSource",
      "removeSource",
      "updateSelectedSite",
      "updateModelParams",
    ]),

    toggleSourcesSection() {
      this.sourcesSectionExpanded = !this.sourcesSectionExpanded;
    },

    onSendToRedaction({ topic, sources, siteId }) {
      this.redacInput = {
        topic: topic || "",
        sources: sources || [],
        siteId: siteId ?? null,
      };
      this.sourcesSectionExpanded = false;
      this.$toast?.success("Sources envoyées vers la rédaction") ||
        console.log("sent to redaction");
    },

    addSourceField() {
      this.addSource("");
    },

    removeSourceField(index) {
      this.removeSource(index);
    },

    async handleScrapeSources() {
      this.isScrapingLoading = true;
      try {
        await this.$store.dispatch("aiFactory/scrapeSources");
        this.$toast?.success("Sources scraped successfully") ||
          console.log("Sources scraped");
      } catch (error) {
        this.$toast?.error("Error scraping sources: " + error.message) ||
          console.error(error);
      } finally {
        this.isScrapingLoading = false;
      }
    },

    async handleExecutePrompt({
      promptType,
      systemPrompt,
      userPrompt,
      modelConfig,
      additionalParams,
    }) {
      this.isPromptLoading = true;
      try {
        const params = {
          promptType,
          systemPrompt,
          userPrompt,
          modelConfig: modelConfig || this.selectedModel,
          additionalParams: additionalParams || {},
        };

        await this.testPrompt(params);
        this.$toast?.success("Prompt executed successfully") ||
          console.log("Prompt executed");
      } catch (error) {
        const missingFields =
          error?.response?.data?.detail?.missing_fields || [];
        const detailMessage = error?.response?.data?.detail?.message;
        const errorMessage =
          missingFields.length > 0
            ? `Missing required fields: ${missingFields.join(", ")}`
            : detailMessage || error.message;

        this.$toast?.error("Error executing prompt: " + errorMessage) ||
          console.error(error);
      } finally {
        this.isPromptLoading = false;
      }
    },

    async handleExecutePipeline({ steps }) {
      if (!Array.isArray(steps) || steps.length === 0) return;

      this.isPromptLoading = true;
      try {
        for (const step of steps) {
          const params = {
            promptType: step.promptType,
            systemPrompt: step.systemPrompt,
            userPrompt: step.userPrompt,
            modelConfig: step.modelConfig || this.selectedModel,
            additionalParams: step.additionalParams || {},
          };
          await this.testPrompt(params);
        }
        this.$toast?.success(
          "Pipeline exécuté: toutes les étapes ont été lancées"
        ) || console.log("Prompt pipeline executed");
      } catch (error) {
        const missingFields =
          error?.response?.data?.detail?.missing_fields || [];
        const detailMessage = error?.response?.data?.detail?.message;
        const errorMessage =
          missingFields.length > 0
            ? `Missing required fields: ${missingFields.join(", ")}`
            : detailMessage || error.message;
        this.$toast?.error("Pipeline interrompu: " + errorMessage) ||
          console.error(error);
      } finally {
        this.isPromptLoading = false;
      }
    },

    async clearPerplexityCache() {
      this.clearingPerplexityCache = true;
      try {
        const response = await axios.post(
          "/api/ai-factory/clear-cache/perplexity"
        );
        const message = response.data.deleted_files
          ? `Perplexity cache cleared: ${response.data.deleted_files} files deleted`
          : "Perplexity cache cleared";
        this.$toast?.success(message) || console.log(message);
      } catch (error) {
        this.$toast?.error(
          "Error clearing Perplexity cache: " + error.message
        ) || console.error(error);
      } finally {
        this.clearingPerplexityCache = false;
      }
    },

    async clearScrapingCache() {
      this.clearingScrapingCache = true;
      try {
        const response = await axios.post(
          "/api/ai-factory/clear-cache/scraping"
        );
        const message = response.data.deleted_files
          ? `Scraping cache cleared: ${response.data.deleted_files} files deleted`
          : "Scraping cache cleared";
        this.$toast?.success(message) || console.log(message);
      } catch (error) {
        this.$toast?.error("Error clearing scraping cache: " + error.message) ||
          console.error(error);
      } finally {
        this.clearingScrapingCache = false;
      }
    },
  },
};
</script>

<style scoped>
.post-create-page {
  background: linear-gradient(180deg, #f7f9fc 0%, #ffffff 240px);
  min-height: calc(100vh - 120px);
  padding-bottom: 24px;
}

.post-create-hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #253746 0%, #40566c 100%);
  border-radius: 16px;
  padding: 20px;
  color: #fff;
}

.post-create-hero__content {
  max-width: 760px;
}

.post-create-hero__eyebrow {
  font-size: 12px;
  text-transform: uppercase;
  opacity: 0.8;
  letter-spacing: 0.08em;
}

.post-create-hero__title {
  margin: 8px 0 10px;
  font-size: 28px;
  line-height: 1.2;
}

.post-create-hero__copy {
  margin: 0;
  opacity: 0.9;
}

.post-create-hero__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.post-create-section {
  border-radius: 14px;
  border-color: #e6ecf2 !important;
}

.post-create-section__title {
  font-size: 1rem;
  font-weight: 700;
}

@media (max-width: 960px) {
  .post-create-hero {
    flex-direction: column;
  }

  .post-create-hero__actions {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
